import { useState, useCallback } from 'react';
import { usePipeline, useRefreshPipeline } from '../services/queries';

export function usePipelineDetails(id: string) {
  const [limit, setLimit] = useState(20);
  const [expandedRuns, setExpandedRuns] = useState<Set<number>>(new Set());

  // useQuery handles initial fetch and deduplication automatically
  const { data: pipeline, isLoading, error } = usePipeline(id, limit);
  const refreshMutation = useRefreshPipeline();

  const recentRuns = pipeline && 'recentRuns' in pipeline ? pipeline.recentRuns : [];

  const handleRefresh = useCallback(async () => {
    try {
      // Use refresh mutation to get fresh data
      await refreshMutation.mutateAsync({ id, limit });
    } catch (error) {
      console.error('Failed to refresh pipeline runs:', error);
    }
  }, [id, limit, refreshMutation]);

  const handleLimitChange = useCallback(async (newLimit: number) => {
    setLimit(newLimit);
    // Use same refresh mutation approach for pagination changes
    try {
      await refreshMutation.mutateAsync({ id, limit: newLimit });
    } catch (error) {
      console.error('Failed to refresh pipeline runs with new limit:', error);
    }
  }, [id, refreshMutation]);

  const handleToggleRunDetails = useCallback((buildId: number) => {
    setExpandedRuns(prev => {
      const newExpanded = new Set(prev);
      if (newExpanded.has(buildId)) {
        newExpanded.delete(buildId);
      } else {
        newExpanded.add(buildId);
      }
      return newExpanded;
    });
  }, []);

  return {
    // Data
    pipeline,
    recentRuns,
    
    // State
    limit,
    expandedRuns,
    isLoading,
    error,
    isRefreshing: refreshMutation.isPending,
    
    // Actions
    handleRefresh,
    handleLimitChange,
    handleToggleRunDetails,
  };
}